import { DocsLayout } from "@/components/docs-layout";
import { DocsNavigation } from "@/components/docs-navigation";
import { TrendingUp, Zap, Shield, Globe, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

const API_URL =
  import.meta.env.VITE_BACKEND_API_URL || "https://api.nordex.trade";

interface MarketBasic {
  symbol: string;
  is_active: boolean;
}

export const Welcome = () => {
  const [markets, setMarkets] = useState<MarketBasic[]>([]);
  const [loadingMarkets, setLoadingMarkets] = useState(true);

  useEffect(() => {
    async function fetchMarkets() {
      try {
        const res = await fetch(`${API_URL}/api/markets`);
        if (!res.ok) throw new Error("Failed");
        const data: MarketBasic[] = await res.json();
        setMarkets(data);
      } catch {
        // Silently fail - will show fallback text
      } finally {
        setLoadingMarkets(false);
      }
    }
    fetchMarkets();
  }, []);

  return (
    <DocsLayout>
      <div className="max-w-4xl mx-auto px-6">
        <h1 className="text-3xl font-bold mb-2">Welcome to Nordex</h1>
        
        <div className="text-sm text-muted-foreground mb-8">
          <p>Your gateway to decentralized perpetual futures trading on Solana</p>
        </div>

        {/* Introduction */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">What is Nordex?</h2>
          <p className="text-muted-foreground mb-6">
            Nordex is a decentralized perpetual futures trading platform built on Solana. 
            Trade cryptocurrencies with leverage up to 100x, manage positions in real-time, 
            and access professional-grade trading tools—all without intermediaries.
          </p>
          <p className="text-muted-foreground mb-6">
            Unlike traditional spot trading where you buy and hold assets, perpetual futures 
            allow you to trade the price movement of cryptocurrencies without actually owning them. 
            This enables both long (betting on price increases) and short (betting on price decreases) positions.
          </p>
        </section>

        {/* Key Features */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Key Features</h2>
          
          <div className="grid gap-4 md:grid-cols-2">
            <div className="p-6 border border-border rounded-lg bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <TrendingUp className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg">Perpetual Futures Trading</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Trade perpetual contracts that track the price of underlying assets. 
                Go long or short with leverage up to 100x across a wide range of cryptocurrency markets.
              </p>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <Zap className="h-6 w-6 text-emerald-500" />
                </div>
                <h3 className="font-semibold text-lg">Real-Time Market Data</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Live price feeds from professional data providers, real-time order books, market trades, 
                and{' '}
                <a 
                  href="https://www.tradingview.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  TradingView
                </a>
                {' '}charts provide you with professional-grade market data.
              </p>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Shield className="h-6 w-6 text-blue-500" />
                </div>
                <h3 className="font-semibold text-lg">Built on Solana</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Leverage Solana's high-speed, low-cost blockchain for fast trade execution 
                and minimal fees. Connect with Phantom, Solflare, Backpack, or use email/social login.
              </p>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <Globe className="h-6 w-6 text-purple-500" />
                </div>
                <h3 className="font-semibold text-lg">Accessible Trading</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                No KYC required. Trade from anywhere in the world with just a wallet connection 
                or email login. Full control of your funds at all times.
              </p>
            </div>
          </div>
        </section>

        {/* Available Markets */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Available Markets</h2>
          <p className="text-muted-foreground mb-6">
            {loadingMarkets
              ? "Loading available markets..."
              : markets.length > 0
                ? `Trade perpetual futures across ${markets.length} cryptocurrency markets:`
                : "Trade perpetual futures across a wide range of cryptocurrency markets:"}
          </p>
          {loadingMarkets ? (
            <div className="flex items-center justify-center py-6 text-muted-foreground gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Loading...</span>
            </div>
          ) : markets.length > 0 ? (
            <div className="grid grid-cols-3 gap-3 md:grid-cols-5">
              {markets.map((market) => (
                <div key={market.symbol} className="p-3 border border-border rounded-lg bg-card text-center">
                  <span className="text-sm font-semibold">{market.symbol}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Visit the <a href="/markets" className="underline underline-offset-2 hover:text-foreground">Available Markets</a> page for the full list.
            </p>
          )}
        </section>

        {/* How It Works */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">How It Works</h2>
          
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                1
              </div>
              <div>
                <h3 className="font-semibold mb-1">Connect Your Wallet or Sign In</h3>
                <p className="text-sm text-muted-foreground">
                  Connect your Solana wallet (Phantom, Solflare, Backpack) or sign in with email/social login.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                2
              </div>
              <div>
                <h3 className="font-semibold mb-1">Deposit Funds</h3>
                <p className="text-sm text-muted-foreground">
                  Deposit USDC or USDT from Solana, Arbitrum, or Ethereum to use as collateral for your positions.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                3
              </div>
              <div>
                <h3 className="font-semibold mb-1">Start Trading</h3>
                <p className="text-sm text-muted-foreground">
                  Select your market, choose your leverage, and place long or short positions.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                4
              </div>
              <div>
                <h3 className="font-semibold mb-1">Manage Positions</h3>
                <p className="text-sm text-muted-foreground">
                  Monitor your open positions, track profit/loss in real-time, and close positions when ready.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Getting Started CTA */}
        <section className="mb-12">
          <div className="p-6 border border-primary/20 rounded-lg bg-primary/5">
            <h3 className="text-lg font-semibold mb-2">Ready to Get Started?</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Follow our step-by-step guide to make your first trade on Nordex.
            </p>
            <p className="text-sm text-muted-foreground">
              Continue to the next section to learn how to connect your wallet.
            </p>
          </div>
        </section>

        <DocsNavigation 
          next={{ title: "Connecting Your Wallet", href: "/connect-wallet" }}
        />
      </div>
    </DocsLayout>
  );
};

